import { css, cx } from "@emotion/css";
import { forwardRef, useImperativeHandle, useMemo, useRef, useCallback } from "react";
import {
  SectionWrapper,
  SectionHeader,
  Option,
  IMentionAttributes,
  MentionSuggestion,
  useOnSuggestionDropdownOpen,
  getMentionExtensionStorage,
} from "../../utils";
import { UnreachableCaseError } from "libs/errors";
import { IListRef, List, ListScrollbox, useFocusFirstEntryOnQueryChange } from "~/components/list";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import { Editor } from "@tiptap/react";
import { useThreadRequest } from "~/hooks/useThreadRequest";
import { DateTimeSuggestion } from "libs/datetime-suggestions/DateTimeSuggestion";
import { DateTime } from "luxon";
import { getDueDateSuggestions } from "./getDueDateSuggestions";
import { SuggestionProps } from "@tiptap/suggestion";
import { DisplayDate } from "~/dialogs/remind-me";
import { SpecialDateEnum } from "libs/datetime-suggestions/utils";
import { useCurrentUserSettings } from "~/hooks/useCurrentUserSettings";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";

type RequestSuggestion = DateTimeSuggestion;

/**
 * This code was largely taken from https://tiptap.dev/api/nodes/mention#usage
 */

export const DueDateDropdown = forwardRef<
  { onKeyDown(o: { event: KeyboardEvent }): boolean | undefined },
  SuggestionProps<MentionSuggestion> & { mention: IMentionAttributes }
>((props, forwardRef) => {
  const listRef = useRef<IListRef<RequestSuggestion>>(null);
  const suggestions = useSuggestions({ editor: props.editor, query: props.query, subject: props.mention });

  useOnSuggestionDropdownOpen();

  const onSelect = useCallback(
    (event: { entry: RequestSuggestion }) => {
      const suggestion = event.entry;

      if (suggestion.template === "request done") {
        const item: IMentionAttributes = {
          ...props.mention,
          requestComplete: true,
        };

        props.command(item);
      } else {
        let dueDate: string;

        const date = suggestion?.getDate(DateTime.now());

        if (!date || !date.isValid) {
          throw new Error("suggestion.date is null");
        }

        switch (suggestion.valueType) {
          case "datetime": {
            dueDate = date.toISO();
            break;
          }
          case "date":
          case "week":
          case "month":
          case "year": {
            dueDate = date.toISODate();
            break;
          }
          case null: {
            throw new Error("suggestion.valueType is null");
          }
          default:
            throw new UnreachableCaseError(suggestion.valueType);
        }

        const item: IMentionAttributes = {
          ...props.mention,
          dueDate,
        };

        props.command(item);
      }
    },
    [props.command],
  );

  useImperativeHandle(forwardRef, () => ({
    onKeyDown: ({ event }) => {
      const list = listRef.current!;

      if (event.key === "ArrowUp") {
        list.focusPrevEntry();
        return true;
      }

      if (event.key === "ArrowDown") {
        list.focusNextEntry();
        return true;
      }

      if (event.key === "Enter") {
        event.stopPropagation();
        const entry = list.focusableOrActiveEntry();
        if (!entry) return false;
        onSelect({ entry: entry.data });
        return true;
      }

      return false;
    },
  }));

  useFocusFirstEntryOnQueryChange({ listRef, query: props.query });

  return (
    <ListScrollbox>
      <div
        data-testid="editor-request-response-dropdown"
        className={cx("bg-white rounded overflow-hidden", `w-[330px] pb-3`, mentionListStyles)}
      >
        <List ref={listRef} mode="active-descendent" onEntryAction={onSelect} focusEntryOnMouseOver>
          <div role="list">
            <SectionWrapper>
              <SectionHeader label={`Response due by`} />

              {suggestions.map(({ suggestion, date }, index) => {
                const id = suggestion.content || suggestion.template;

                return (
                  <Option key={id} item={suggestion} relativeOrder={index}>
                    <div className="inline-flex items-center shrink whitespace-nowrap overflow-hidden mx-3">
                      <span className="truncate">{suggestion.content}</span>
                    </div>

                    <div className="flex-1" />

                    {date && (
                      <div>
                        <DisplayDate date={date} isEOD={suggestion.valueType !== "datetime"} />
                      </div>
                    )}
                  </Option>
                );
              })}
            </SectionWrapper>
          </div>
        </List>
      </div>
    </ListScrollbox>
  );
});

const mentionListStyles = css`
  box-shadow:
    rgba(15, 15, 15, 0.05) 0px 0px 0px 1px,
    rgba(15, 15, 15, 0.1) 0px 3px 6px,
    rgba(15, 15, 15, 0.2) 0px 9px 24px;
`;

/* -----------------------------------------------------------------------------------------------*/

function useSuggestions(props: {
  editor: Editor;
  query: string;
  subject: IMentionAttributes;
}): Array<{ suggestion: RequestSuggestion; date: DateTime<true> | DateTime<false> | null }> {
  const environment = useClientEnvironment();
  const { currentUserId } = useAuthGuardContext();
  // Note that we're getting the thread ID from the mention extension's storage, not the
  // due date extension's storage.
  const { threadId } = getMentionExtensionStorage(props.editor);

  const [threadRequest] = useThreadRequest({
    threadId,
    senderUserId: props.subject.subject === "user" ? props.subject.id : null,
    recipientUserId: currentUserId,
  });

  const { settings } = useCurrentUserSettings();

  return useMemo(() => {
    const now = DateTime.now();
    const suggestions = getDueDateSuggestions(environment, {
      query: props.query,
      threadRequest,
      defaultDueDateSuggestion: settings?.default_request_due_date_suggestion ?? null,
    });

    return (
      suggestions
        .map((suggestion) => {
          return {
            suggestion,
            date: suggestion.getDate(now),
          };
        })
        // @todo(jorroll): support someday
        .filter((s) => s.date !== SpecialDateEnum.SOMEDAY)
    );
  }, [props.query, threadRequest, settings, environment]);
}

/* -----------------------------------------------------------------------------------------------*/
